import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Clock, Plus, X, ChevronDown, ChevronRight } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { fetchAllRows } from '@/utils/supabasePagination'
import { useAuth } from '@/contexts/AuthContext'

interface Choice {
  id: string
  label: string
  text: string
  is_correct: boolean
}
interface Question {
  id: string
  text: string
  category: string
  choices: Choice[]
}

interface CategorySelection {
  id: string
  category: string
  questionCount: number
  weight: number
}

interface TestFormProps {
  editingTest?: any
  onComplete: () => void
}

/**
 * Test Creation Modes:
 *
 * 1. Manual:
 *    - User manually selects all questions from the available pool.
 *    - All selected questions are directly linked to the test (no randomization, no category/weight rules).
 *    - Questions are inserted into the 'test_questions' table after test creation.
 *
 * 2. Random:
 *    - User specifies the number of random questions per category and their weight.
 *    - The sum of all category weights must be exactly 100.
 *    - When the test is assigned, questions are drawn randomly according to these rules.
 *    - No manual question selection.
 *    - All category/weight info is stored as JSON in the 'categories' column of the test.
 *
 * 3. Hybrid:
 *    - User combines both approaches: selects some questions manually and defines random selection rules for others.
 *    - Both manual and random questions will be included in the test.
 *    - Used when some questions must be the same for all employees, and the rest are random.
 */

const TestForm = ({ editingTest, onComplete }: TestFormProps) => {
  const { toast } = useToast()
  const { companyId } = useAuth()

  const [questionsByCategory, setQuestionsByCategory] = useState<Record<string, Question[]>>({})
  const [allCategories, setAllCategories] = useState<string[]>([])
  const [loadingQuestions, setLoadingQuestions] = useState(true)
  const [loadingTestData, setLoadingTestData] = useState(false)
  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [duration, setDuration] = useState(60)
  const [selectionMode, setSelectionMode] = useState<'manual' | 'random' | 'hybrid'>('manual')
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([])
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [categorySelections, setCategorySelections] = useState<CategorySelection[]>([
    {
      id: Math.random().toString(36).slice(2),
      category: '',
      questionCount: 1,
      weight: 25,
    },
  ])
  const [manualWeight, setManualWeight] = useState(50)

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoadingQuestions(true)
      let allData: any[]
      try {
        allData = await fetchAllRows(
          'questions',
          'id, text, category, choices(id,label,text,is_correct)',
          (q) => q.order('category').order('id', { foreignTable: 'choices' })
        )
      } catch (error: any) {
        toast({
          title: 'Sorular çekilemedi',
          description: error.message,
          variant: 'destructive',
        })
        setLoadingQuestions(false)
        return
      }

      const grouped: Record<string, Question[]> = {}
      allData.forEach((q: any) => {
        const cat = q.category || 'Diğer'
        if (!grouped[cat]) grouped[cat] = []
        grouped[cat].push({
          id: q.id,
          text: q.text,
          category: cat,
          choices: (q.choices || []).sort((a: Choice, b: Choice) => a.label.localeCompare(b.label)),
        })
      })
      setQuestionsByCategory(grouped)
      setAllCategories(Object.keys(grouped))
      setLoadingQuestions(false)
    }
    fetchQuestions()
  }, [])

  // Edit mode - fetch test data
  useEffect(() => {
    const fetchTestData = async () => {
      if (!editingTest?.id) return

      setLoadingTestData(true)
      try {
        // Fetch test details
        const { data: testData, error: testError } = await supabase
          .from('tests')
          .select('*')
          .eq('id', editingTest.id)
          .single()

        if (testError) throw testError

        // Set basic test info
        setName(testData.name || '')
        setDescription(testData.description || '')
        setDuration(Math.floor((testData.time_limit || 60) / 60))
        setSelectionMode(testData.type || 'manual')

        // Fetch manually selected questions if type is manual or hybrid
        if (testData.type === 'manual' || testData.type === 'hybrid') {
          const { data: testQuestions, error: questionsError } = await supabase
            .from('test_questions')
            .select('question_id')
            .eq('test_id', editingTest.id)

          if (questionsError) throw questionsError

          const questionIds = testQuestions.map((tq) => tq.question_id)
          setSelectedQuestions(questionIds)
        }

        // Parse categories for random/hybrid
        if ((testData.type === 'random' || testData.type === 'hybrid') && testData.categories) {
          const categoriesData = testData.categories
          const selections: CategorySelection[] = []

          Object.entries(categoriesData).forEach(([category, info]: [string, any]) => {
            if (category !== 'Manual') {
              selections.push({
                id: Math.random().toString(36).slice(2),
                category: category,
                questionCount: info.no_questions || 1,
                weight: info.weight || 0,
              })
            } else if (category === 'Manual' && testData.type === 'hybrid') {
              // Set manual weight for hybrid mode
              setManualWeight(info.weight || 50)
            }
          })

          if (selections.length > 0) {
            setCategorySelections(selections)
          }
        }
      } catch (error) {
        console.error('Error fetching test data:', error)
        toast({
          title: 'Test verileri yüklenemedi',
          description: error.message,
          variant: 'destructive',
        })
      } finally {
        setLoadingTestData(false)
      }
    }

    fetchTestData()
  }, [editingTest])

  const toggleCategory = (cat: string) => {
    setExpandedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    )
  }

  const handleQuestionToggle = (id: string) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((qid) => qid !== id) : [...prev, id]
    )
  }

  const handleCategorySelectAll = (category: string, checked: boolean) => {
    const categoryQuestionIds = questionsByCategory[category]?.map((q) => q.id) || []
    setSelectedQuestions((prev) => {
      if (checked) {
        // Add all questions from this category that aren't already selected
        const newSelections = [...prev]
        categoryQuestionIds.forEach((id) => {
          if (!newSelections.includes(id)) {
            newSelections.push(id)
          }
        })
        return newSelections
      } else {
        // Remove all questions from this category
        return prev.filter((id) => !categoryQuestionIds.includes(id))
      }
    })
  }

  const isCategoryFullySelected = (category: string) => {
    const categoryQuestionIds = questionsByCategory[category]?.map((q) => q.id) || []
    return (
      categoryQuestionIds.length > 0 &&
      categoryQuestionIds.every((id) => selectedQuestions.includes(id))
    )
  }

  const isCategoryPartiallySelected = (category: string) => {
    const categoryQuestionIds = questionsByCategory[category]?.map((q) => q.id) || []
    const selectedCount = categoryQuestionIds.filter((id) => selectedQuestions.includes(id)).length
    return selectedCount > 0 && selectedCount < categoryQuestionIds.length
  }

  const addCategorySelection = () => {
    setCategorySelections((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).slice(2),
        category: '',
        questionCount: 1,
        weight: 25,
      },
    ])
  }

  const removeCategorySelection = (id: string) => {
    setCategorySelections((prev) => prev.filter((s) => s.id !== id))
  }

  const updateCategorySelection = (
    id: string,
    field: 'category' | 'questionCount' | 'weight',
    value: any
  ) => {
    setCategorySelections((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)))
  }

  const getUsedCategories = () => categorySelections.map((s) => s.category).filter(Boolean)

  const getAvailableCategoriesForSelection = (currentCategory?: string) =>
    allCategories.filter((cat) => !getUsedCategories().includes(cat) || cat === currentCategory)

  const getMaxQuestionsForCategory = (cat: string) => questionsByCategory[cat]?.length || 1

  const getTotalRandomQuestions = () =>
    categorySelections.reduce(
      (sum, s) => sum + (s.category && s.questionCount ? s.questionCount : 0),
      0
    )

  const questions = Object.values(questionsByCategory).flat()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 1. Validation
    if (!name.trim() || !description.trim() || duration < 1) {
      toast({
        title: 'Eksik bilgi',
        description: 'Tüm alanları doldurun.',
        variant: 'destructive',
      })
      return
    }
    if (
      (selectionMode === 'manual' && selectedQuestions.length === 0) ||
      (selectionMode === 'random' && getTotalRandomQuestions() === 0) ||
      (selectionMode === 'hybrid' &&
        selectedQuestions.length === 0 &&
        getTotalRandomQuestions() === 0)
    ) {
      toast({
        title: 'Soru eksik',
        description: 'En az bir soru seçmelisiniz.',
        variant: 'destructive',
      })
      return
    }

    // Total weight check
    const totalWeight =
      categorySelections.reduce((sum, s) => sum + (s.category && s.weight ? s.weight : 0), 0) +
      (selectionMode === 'hybrid' && selectedQuestions.length > 0 ? manualWeight : 0)

    if ((selectionMode === 'random' || selectionMode === 'hybrid') && totalWeight !== 100) {
      toast({
        title: 'Toplam ağırlık 100 olmalı',
        description:
          "Toplam kategori ağırlığı 100'den az veya fazla olamaz. Lütfen ağırlık seçimini düzenleyin.",
        variant: 'destructive',
      })
      return
    }

    const manualQuestionsCount = selectedQuestions.length
    const randomQuestionsCount = getTotalRandomQuestions()
    let totalQuestions = 0
    if (selectionMode === 'manual') {
      totalQuestions = manualQuestionsCount
    } else if (selectionMode === 'random') {
      totalQuestions = randomQuestionsCount
    } else if (selectionMode === 'hybrid') {
      totalQuestions = manualQuestionsCount + randomQuestionsCount
    }

    // Prepare categories JSON
    const isRandom = selectionMode !== 'manual'
    let categoriesJSON = null
    if (isRandom) {
      categoriesJSON = {}
      categorySelections
        .filter((s) => s.category && s.questionCount > 0)
        .forEach((s) => {
          categoriesJSON[s.category] = {
            weight: s.weight,
            no_questions: s.questionCount,
          }
        })
      if (selectionMode === 'hybrid' && selectedQuestions.length > 0) {
        categoriesJSON['Manual'] = {
          weight: manualWeight,
          no_questions: selectedQuestions.length,
        }
      }
      if (Object.keys(categoriesJSON).length === 0) categoriesJSON = null
    }

    let testId = editingTest?.id

    try {
      if (editingTest?.id) {
        // UPDATE existing test
        const { error: updateError } = await supabase
          .from('tests')
          .update({
            name: name.trim(),
            description: description.trim(),
            time_limit: duration * 60,
            categories: categoriesJSON,
            type: selectionMode,
            total_questions: totalQuestions,
          })
          .eq('id', editingTest.id)

        if (updateError) throw updateError

        // delete existing test_questions when editing
        // This handles mode changes (manual/hybrid -> random)
        const { error: deleteError } = await supabase
          .from('test_questions')
          .delete()
          .eq('test_id', editingTest.id)

        if (deleteError) throw deleteError

        // Re-insert test_questions only if current mode is manual or hybrid
        if (selectionMode === 'manual' || selectionMode === 'hybrid') {
          const uniqueQuestionIds = Array.from(new Set(selectedQuestions))
          if (uniqueQuestionIds.length > 0) {
            const insertRows = uniqueQuestionIds.map((question_id, index) => ({
              test_id: editingTest.id,
              question_id,
              created_at: new Date().toISOString(),
              company_id: companyId,
            }))

            const { error: qInsertError } = await supabase.from('test_questions').insert(insertRows)
            if (qInsertError) throw qInsertError
          }
        }
      } else {
        // INSERT new test
        const { data, error } = await supabase
          .from('tests')
          .insert([
            {
              name: name.trim(),
              description: description.trim(),
              time_limit: duration * 60,
              randomized: isRandom,
              categories: categoriesJSON,
              created_at: new Date().toISOString(),
              type: selectionMode,
              total_questions: totalQuestions,
            },
          ])
          .select()

        if (error || !data || !data.length) {
          throw error || new Error('Test kaydı döndürülmedi.')
        }

        testId = data[0].id

        // Insert selected questions for manual/hybrid
        if (selectionMode === 'manual' || selectionMode === 'hybrid') {
          const uniqueQuestionIds = Array.from(new Set(selectedQuestions))
          if (uniqueQuestionIds.length > 0) {
            const insertRows = uniqueQuestionIds.map((question_id, index) => ({
              test_id: testId,
              question_id,
              created_at: new Date().toISOString(),
              company_id: companyId,
            }))

            const { error: qInsertError } = await supabase.from('test_questions').insert(insertRows)
            if (qInsertError) throw qInsertError
          }
        }
      }

      toast({
        title: editingTest ? 'Sınav güncellendi' : 'Sınav oluşturuldu',
        description: 'Kayıt başarılı.',
      })

      // Reset form
      setName('')
      setDescription('')
      setDuration(60)
      setSelectedQuestions([])
      setSelectionMode('manual')
      setCategorySelections([
        {
          id: Math.random().toString(36).slice(2),
          category: '',
          questionCount: 1,
          weight: 25,
        },
      ])
      setManualWeight(50)
      onComplete()
    } catch (err) {
      toast({
        title: editingTest ? 'Sınav güncellenemedi' : 'Sınav kaydedilemedi',
        description: typeof err === 'object' ? JSON.stringify(err) : String(err),
        variant: 'destructive',
      })
    }
  }

  if (loadingTestData) {
    return (
      <Card className="w-full shadow-md">
        <CardContent className="p-8 text-center">
          <p>Test verileri yükleniyor...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full shadow-md">
      <CardHeader>
        <CardTitle>{editingTest ? 'Sınavı Düzenle' : 'Yeni Sınav Oluştur'}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Sınav Adı</Label>
            <Input
              id="name"
              placeholder="Sınav başlığı"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Açıklama</Label>
            <Textarea
              id="description"
              placeholder="Sınav hakkında açıklama"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Sınav Süresi (dakika)
            </Label>
            <Input
              id="duration"
              type="number"
              min={1}
              max={480}
              placeholder="60"
              value={duration}
              onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 60))}
              required
            />
          </div>

          <div className="space-y-4">
            <Label>Soru Seçimi Yöntemi</Label>
            <RadioGroup
              value={selectionMode}
              onValueChange={(v) => setSelectionMode(v as any)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual">Manuel olarak seç</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="random" id="random" />
                <Label htmlFor="random">Rastgele seç</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hybrid" id="hybrid" />
                <Label htmlFor="hybrid">Manuel & Rastgele Seç</Label>
              </div>
            </RadioGroup>

            {(selectionMode === 'random' || selectionMode === 'hybrid') && (
              <div className="space-y-4 pt-2 bg-gray-50 p-4 rounded-md">
                <div className="space-y-2">
                  <Label>Kategori Bazında Rastgele Soru Seçimi</Label>
                  <p className="text-sm text-gray-600">
                    {selectionMode === 'hybrid'
                      ? 'Manuel seçilen sorulara ek olarak her kategoriden kaç soru rastgele seçileceğini belirleyin'
                      : 'Her kategoriden kaç soru seçileceğini belirleyin'}
                  </p>
                </div>
                <div className="space-y-3">
                  {categorySelections.map((selection, index) => (
                    <div
                      key={selection.id}
                      className="flex items-center gap-3 p-3 bg-white rounded border"
                    >
                      <div className="flex-1">
                        <Select
                          value={selection.category}
                          onValueChange={(value) =>
                            updateCategorySelection(selection.id, 'category', value)
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Kategori seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableCategoriesForSelection(selection.category).map(
                              (category) => (
                                <SelectItem key={category} value={category}>
                                  {category} ({getMaxQuestionsForCategory(category)} soru)
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Soru sayısı */}
                      <div className="flex items-center gap-2 w-40">
                        <Label className="text-xs text-gray-500 w-20">Soru Sayısı:</Label>
                        <Input
                          type="number"
                          min={1}
                          max={
                            selection.category ? getMaxQuestionsForCategory(selection.category) : 1
                          }
                          value={selection.questionCount}
                          onChange={(e) =>
                            updateCategorySelection(
                              selection.id,
                              'questionCount',
                              Math.max(
                                1,
                                Math.min(
                                  parseInt(e.target.value) || 1,
                                  selection.category
                                    ? getMaxQuestionsForCategory(selection.category)
                                    : 1
                                )
                              )
                            )
                          }
                          className="w-20"
                          disabled={!selection.category}
                        />
                      </div>
                      {/* Ağırlık */}
                      <div className="flex items-center gap-2 w-36">
                        <Label className="text-xs text-gray-500 w-12">Ağırlık:</Label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={selection.weight}
                          onChange={(e) =>
                            updateCategorySelection(
                              selection.id,
                              'weight',
                              Math.max(0, Math.min(100, parseInt(e.target.value) || 0))
                            )
                          }
                          className="w-20"
                          disabled={!selection.category}
                        />
                      </div>
                      {/* Silme butonu */}
                      {categorySelections.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCategorySelection(selection.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCategorySelection}
                    className="w-full"
                    disabled={getUsedCategories().length >= allCategories.length}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Kategori Ekle
                  </Button>
                </div>
                {getTotalRandomQuestions() > 0 && (
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-sm font-medium text-blue-800">
                      Rastgele Seçilecek Soru: {getTotalRandomQuestions()}
                      {selectionMode === 'hybrid' && selectedQuestions.length > 0 && (
                        <span className="ml-2">+ Manuel Seçilen: {selectedQuestions.length}</span>
                      )}
                    </p>
                    <div className="mt-2 text-xs text-blue-600">
                      {categorySelections
                        .filter((s) => s.category && s.questionCount > 0)
                        .map((s) => `${s.category} (${s.questionCount})`)
                        .join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {(selectionMode === 'manual' || selectionMode === 'hybrid') && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-baseline gap-2">
                  <Label className="font-medium">
                    {selectionMode === 'hybrid' ? 'Manuel Soru Seçimi' : 'Sorular'}
                  </Label>
                  {selectionMode === 'hybrid' && (
                    <span className="text-sm text-gray-600">
                      (Bu sorulara ek olarak rastgele sorular da eklenecek)
                    </span>
                  )}
                </div>
                {selectionMode === 'hybrid' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Ağırlık</span>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={manualWeight}
                      onChange={(e) =>
                        setManualWeight(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))
                      }
                      className="w-16 h-8"
                      style={{ fontSize: '0.95rem' }}
                    />
                  </div>
                )}
              </div>
              {questions.length > 0 ? (
                <div className="border rounded-md max-h-96 overflow-y-auto bg-gray-50">
                  {Object.entries(questionsByCategory).map(([category, categoryQuestions]) => (
                    <Collapsible key={category} open={expandedCategories.includes(category)}>
                      <CollapsibleTrigger
                        asChild
                        className="w-full bg-gray-100 px-4 py-3 font-medium text-left flex items-center justify-between hover:bg-gray-200 transition-colors border-b"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={
                              isCategoryFullySelected(category)
                                ? true
                                : isCategoryPartiallySelected(category)
                                  ? 'indeterminate'
                                  : false
                            }
                            onCheckedChange={(checked) =>
                              handleCategorySelectAll(category, checked === true)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div
                            className="flex items-center justify-between flex-1 cursor-pointer"
                            onClick={() => toggleCategory(category)}
                          >
                            <span>
                              {category} ({categoryQuestions.length} soru)
                            </span>
                            {expandedCategories.includes(category) ? (
                              <ChevronDown className="ml-2 h-4 w-4" />
                            ) : (
                              <ChevronRight className="ml-2 h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="divide-y">
                          {categoryQuestions.map((question, i) => (
                            <div key={question.id} className="p-3 flex items-start gap-3 bg-white">
                              <Checkbox
                                id={`question-${question.id}`}
                                checked={selectedQuestions.includes(question.id)}
                                onCheckedChange={() => handleQuestionToggle(question.id)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <Label
                                  htmlFor={`question-${question.id}`}
                                  className="font-medium cursor-pointer"
                                >
                                  Soru {i + 1}
                                </Label>
                                <div
                                  dangerouslySetInnerHTML={{ __html: question.text }}
                                  className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ol:my-2 prose-ul:my-2 prose-li:my-0.5 mt-1 text-sm text-gray-700"
                                />
                                <div className="grid grid-cols-2 gap-1 mt-2">
                                  {question.choices.map((option) => (
                                    <div
                                      key={option.id}
                                      className={`text-xs flex items-center gap-1 rounded px-2 py-1 ${
                                        option.is_correct
                                          ? 'bg-green-50 text-green-700 font-semibold'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}
                                    >
                                      <span className="w-5 h-5 flex items-center justify-center font-bold">
                                        {option.label.toUpperCase()}
                                      </span>
                                      <span className="truncate">{option.text}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-md">
                  <p className="text-gray-500">Henüz soru eklenmemiş.</p>
                  <Button
                    variant="link"
                    onClick={() => (window.location.href = '/admin/questions')}
                  >
                    Soru eklemek için tıklayın
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button type="button" variant="outline" onClick={onComplete}>
            İptal
          </Button>
          <Button
            type="submit"
            disabled={
              questions.length === 0 ||
              duration <= 0 ||
              (selectionMode === 'manual' && selectedQuestions.length === 0) ||
              (selectionMode === 'random' && getTotalRandomQuestions() === 0) ||
              (selectionMode === 'hybrid' &&
                selectedQuestions.length === 0 &&
                getTotalRandomQuestions() === 0)
            }
          >
            {editingTest ? 'Güncelle' : 'Oluştur'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

export default TestForm
